"use client";

import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, Check, Truck, Zap } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { useHomeStore } from "@/lib/store/home-store";
import { cn } from "@/lib/utils";

const DIET_BADGES = [
	{ label: "Gluten-free", color: "bg-emerald-500" },
	{ label: "Dairy-free", color: "bg-blue-500" },
	{ label: "Soy-free", color: "bg-purple-500" },
	{ label: "No Seed Oil", color: "bg-amber-500" },
] as const;

const FEATURES = [
	{ icon: Check, label: "Subscribe & Save" },
	{ icon: Truck, label: "Ships nationwide" },
	{ icon: Zap, label: "Ready in minutes" },
] as const;

const STATS = [
	{ label: "Menu Options", value: "40+" },
	{ label: "Protein", value: "35-90g" },
	{ label: "Cook time", value: "5-6 min" },
] as const;

import { IMAGES } from "@/lib/constants/images";

const HERO_IMAGES = IMAGES.home.hero;

const HERO_CONTENT = {
	headline: "Next-Day Frozen Meals",
	headlineAccent: "Built For High-Performers.",
	description:
		"High-protein, chef-crafted meals delivered frozen fresh - at your door tomorrow.",
	ctaPrimary: "Build A Box",
} as const;

function TypingText({
	text,
	delay = 0,
	className,
}: {
	text: string;
	delay?: number;
	className?: string;
}) {
	const [displayedText, setDisplayedText] = useState("");
	const [started, setStarted] = useState(false);
	const hasDot = text.endsWith(".");
	const mainText = hasDot ? text.slice(0, -1) : text;

	useEffect(() => {
		const timer = setTimeout(() => setStarted(true), delay);
		return () => clearTimeout(timer);
	}, [delay]);

	useEffect(() => {
		if (!started) return;
		let i = 0;
		setDisplayedText("");

		const interval = setInterval(() => {
			if (i < mainText.length) {
				setDisplayedText(mainText.slice(0, i + 1));
				i++;
			} else {
				clearInterval(interval);
			}
		}, 150);
		return () => clearInterval(interval);
	}, [started, mainText]);

	if (!started)
		return <span className={cn(className, "opacity-0")}>{text}</span>;

	return (
		<span className={className}>
			{displayedText}
			<motion.span
				initial={{ opacity: 0 }}
				animate={{
					opacity: displayedText.length >= mainText.length ? [1, 0] : 1,
				}}
				transition={{ duration: 0.8, repeat: Infinity, repeatType: "reverse" }}
			>
				{hasDot ? "." : ""}
			</motion.span>
		</span>
	);
}

export function HeroSection({
	headline = HERO_CONTENT.headline,
	headlineAccent = HERO_CONTENT.headlineAccent,
	description = HERO_CONTENT.description,
}: {
	headline?: string;
	headlineAccent?: string;
	description?: string;
}) {
	const currentImage = useHomeStore((state) => state.currentHeroImageIndex);
	const nextHeroImage = useHomeStore((state) => state.nextHeroImage);

	useEffect(() => {
		const timer = setInterval(() => {
			nextHeroImage(HERO_IMAGES.length);
		}, 5000);
		return () => clearInterval(timer);
	}, [nextHeroImage]);

	return (
		// -mt-16: pull section up behind transparent navbar (64px).
		// min-h uses a CSS var set by the announcement bar client so the section
		// is exactly one viewport tall whether the bar is visible or not.
		<section
			className="relative w-full flex items-center bg-background text-foreground overflow-hidden -mt-16"
			style={{ minHeight: "calc(100dvh - var(--announcement-bar-h, 0px))" }}
		>
			<div className="absolute inset-0 z-0 overflow-hidden">
				{/* Always render a base layer with the current image — no animation on first mount */}
				<AnimatePresence initial={false}>
					<motion.div
						key={currentImage}
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						exit={{ opacity: 0 }}
						transition={{ duration: 1.2, ease: "easeInOut" }}
						className="absolute inset-0 w-full h-full will-change-[opacity]"
					>
						<Image
							src={HERO_IMAGES[currentImage]}
							alt="Delicious Ice Age Meals ready to eat"
							fill
							className="object-cover dark:brightness-50"
							priority
							loading="eager"
							sizes="100vw"
							quality={75}
						/>
						<div className="absolute inset-0 bg-linear-to-r from-background/95 via-background/70 to-transparent hidden sm:block" />
						<div className="absolute inset-0 bg-background/75 sm:hidden" />
					</motion.div>
				</AnimatePresence>
			</div>

			<div className="container relative z-10 mx-auto px-4 pt-20 sm:pt-24 pb-32 sm:pb-24 xl:pb-20">
				<motion.div
					initial={{ opacity: 0, y: 20 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.5, delay: 0 }}
					className="max-w-2xl space-y-6 sm:space-y-8"
				>
					<div className="flex items-center gap-1.5">
						<span className="text-amber-400 text-base leading-none">★★★★★</span>
						<span className="text-sm font-semibold text-foreground">
							4.9 out of 5
						</span>
					</div>

					<div className="flex flex-wrap gap-2">
						{DIET_BADGES.map((badge, i) => (
							<motion.span
								key={badge.label}
								initial={{ opacity: 0, scale: 0.8 }}
								animate={{ opacity: 1, scale: 1 }}
								transition={{ duration: 0.3, delay: 0.1 + i * 0.05 }}
								className="inline-flex items-center gap-1.5 rounded-full bg-background/90 backdrop-blur-xl px-4 py-2 text-xs sm:text-sm font-semibold text-foreground shadow-md ring-1 ring-border/50 hover:bg-background/95 transition-all hover:scale-105"
							>
								<span className={`h-2.5 w-2.5 rounded-full ${badge.color}`} />
								{badge.label}
							</motion.span>
						))}
					</div>
					<div className="space-y-4">
						<h1 className="text-2xl sm:text-4xl md:text-4xl lg:text-5xl xl:text-5xl font-extrabold tracking-tight text-foreground leading-[1.15]">
							{headline}
							<TypingText
								text={headlineAccent}
								delay={50}
								className="text-primary block mt-1 sm:mt-2"
							/>
						</h1>
						<motion.p
							initial={{ opacity: 0 }}
							animate={{ opacity: 1 }}
							transition={{ duration: 0.5, delay: 0.1 }}
							className="text-base sm:text-lg text-muted-foreground max-w-lg leading-relaxed md:text-xl"
						>
							{description}
						</motion.p>
					</div>

					<motion.div
						initial={{ opacity: 0, y: 20 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.4, delay: 0.2 }}
						className="flex flex-col sm:flex-row gap-3"
					>
						<Button
							size="lg"
							className="h-12 sm:h-14 px-6 sm:px-8 text-base font-bold rounded-full w-full sm:w-auto shadow-lg hover:shadow-xl transition-all"
							asChild
						>
							<Link href="/build-a-box">
								{HERO_CONTENT.ctaPrimary}
								<ArrowRight className="ml-2 h-4 w-4" />
							</Link>
						</Button>
					</motion.div>

					<motion.div
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						transition={{ duration: 0.5, delay: 0.3 }}
						className="flex flex-wrap gap-4 sm:gap-6 pt-4 sm:pt-8 border-t border-border"
					>
						{FEATURES.map((feature) => (
							<div
								key={feature.label}
								className="flex items-center gap-2 text-sm font-medium text-muted-foreground"
							>
								<feature.icon className="h-4 w-4 text-emerald-500" />
								{feature.label}
							</div>
						))}
					</motion.div>

					{/* Stats — mobile only; desktop shows the absolute-positioned block below */}
					<motion.div
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						transition={{ duration: 0.5, delay: 0.4 }}
						className="flex gap-6 pt-2 lg:hidden"
					>
						{STATS.map((stat) => (
							<div key={stat.label}>
								<p className="text-xl font-black text-foreground">
									{stat.value}
								</p>
								<p className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest">
									{stat.label}
								</p>
							</div>
						))}
					</motion.div>
				</motion.div>
			</div>

			<div className="absolute bottom-0 right-16 p-6 sm:p-8 hidden lg:flex gap-6 sm:gap-8">
				{STATS.map((stat, i) => (
					<motion.div
						key={stat.label}
						initial={{ opacity: 0, y: 20 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.5, delay: 2 + i * 0.2 }}
						className="text-right"
					>
						<p className="text-4xl font-black text-foreground">{stat.value}</p>
						<p className="text-sm font-bold text-muted-foreground uppercase tracking-widest">
							{stat.label}
						</p>
					</motion.div>
				))}
			</div>
		</section>
	);
}
